import { useEffect } from 'react';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';

/*
 * Studio X smooth scroll: lerp 0.165, wheelMultiplier 1.25. Touch stays native.
 * One shared instance, so anchor links, the menu and modals can all drive it.
 * Skipped entirely for prefers-reduced-motion.
 */
let instance: Lenis | null = null;
let locks = 0;

export function startSmoothScroll(): () => void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return () => undefined;
    }
    instance = new Lenis({ autoRaf: true, lerp: 0.165, wheelMultiplier: 1.25, smoothWheel: true });
    // An overlay (the loader) may already be holding the lock.
    if (locks > 0) instance.stop();
    return () => {
        instance?.destroy();
        instance = null;
    };
}

/* Glide to an element (or a Y offset); falls back to native scrolling. */
export function scrollToTarget(target: HTMLElement | number, offset = 0) {
    if (instance) {
        instance.scrollTo(target, { offset, duration: 1.4 });
        return;
    }
    const top = typeof target === 'number' ? target : target.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: top + offset, behavior: 'smooth' });
}

/* Make the scroller re-read the page height (sections that loaded late change it). */
export function refreshScroll() {
    instance?.resize();
}

/* Jump to the top at once (a new page starts at the top; no glide across a page that is gone). */
export function jumpToTop() {
    if (instance) instance.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
}

/* Glide to a section by id. The dock nav floats, so there is no header to clear. */
export function goToSection(id: string) {
    const el = document.getElementById(id);
    if (!el) return;
    scrollToTarget(el, 0);
}

/*
 * Freeze wheel scrolling behind overlays. Counted, so a modal opening on top of
 * the menu cannot release the lock when it closes.
 */
export function lockScroll(locked: boolean) {
    locks = Math.max(0, locks + (locked ? 1 : -1));
    if (!instance) return;
    if (locks > 0) instance.stop();
    else instance.start();
}

export function useScrollLock(locked: boolean) {
    useEffect(() => {
        if (!locked) return;
        lockScroll(true);
        return () => lockScroll(false);
    }, [locked]);
}
